/**
 * LeafletMapPublicView - Public Map Viewer Component using Leaflet
 * Component cho việc xem map public
 *
 * Features:
 * - Display markers with custom colors
 * - Display paths/shapes with animations
 * - Click to select marker
 * - Popup info windows
 */
import { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { TILE_LAYERS, GOOGLE_STYLE_MAP } from './LeafletMap'

// Default center: Da Nang
const DEFAULT_CENTER = [16.0544, 108.2022]
const DEFAULT_ZOOM = 13

// CSS for path animations
const PATH_ANIMATION_STYLES = `
  /* Marching ants animation for dashed lines */
  @keyframes dash-march {
    to {
      stroke-dashoffset: -20;
    }
  }

  /* Pulse animation for shapes */
  @keyframes path-pulse {
    0%, 100% {
      opacity: 1;
      stroke-width: var(--base-stroke-width);
    }
    50% {
      opacity: 0.6;
      stroke-width: calc(var(--base-stroke-width) + 2);
    }
  }

  /* Glow animation */
  @keyframes path-glow {
    0%, 100% {
      filter: drop-shadow(0 0 2px var(--stroke-color));
    }
    50% {
      filter: drop-shadow(0 0 8px var(--stroke-color)) drop-shadow(0 0 12px var(--stroke-color));
    }
  }

  .path-animation-march path,
  .path-animation-march {
    animation: dash-march 0.5s linear infinite;
  }

  .path-animation-pulse path,
  .path-animation-pulse {
    animation: path-pulse 2s ease-in-out infinite;
  }

  .path-animation-glow path,
  .path-animation-glow {
    animation: path-glow 2s ease-in-out infinite;
  }
`

// Inject path animation styles
let pathStylesInjected = false
function injectPathStyles() {
  if (pathStylesInjected) return
  const style = document.createElement('style')
  style.textContent = PATH_ANIMATION_STYLES
  document.head.appendChild(style)
  pathStylesInjected = true
}

// Adjust color brightness
function adjustColor(color, amount) {
  const clamp = (num) => Math.min(255, Math.max(0, num))

  // Parse hex color
  let hex = color.replace('#', '')
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
  }

  const r = clamp(parseInt(hex.substring(0, 2), 16) + amount)
  const g = clamp(parseInt(hex.substring(2, 4), 16) + amount)
  const b = clamp(parseInt(hex.substring(4, 6), 16) + amount)

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

// Create pin marker icon (small dot at exact location)
function createPinIcon(color, isSelected = false) {
  const size = isSelected ? 14 : 10
  return L.divIcon({
    className: 'pin-marker',
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        border: 2px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ${isSelected ? 'box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.4), 0 2px 4px rgba(0,0,0,0.3);' : ''}
      "></div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

// Smart label offset to reduce overlap
function calculateLabelOffset(markerData, allMarkers, map) {
  if (!map || allMarkers.length < 2) return { x: 0, y: 0, hasOffset: false }

  const myPoint = map.latLngToContainerPoint([
    parseFloat(markerData.latitude),
    parseFloat(markerData.longitude),
  ])

  let offsetX = 0
  let offsetY = 0
  const threshold = 140 // pixels - detection range for overlap

  allMarkers.forEach((other) => {
    if (other.id === markerData.id) return

    const otherPoint = map.latLngToContainerPoint([
      parseFloat(other.latitude),
      parseFloat(other.longitude),
    ])

    const dx = myPoint.x - otherPoint.x
    const dy = myPoint.y - otherPoint.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance < threshold && distance > 0) {
      // Push label away from nearby marker
      const pushStrength = (threshold - distance) / threshold * 40
      offsetX += (dx / distance) * pushStrength
      offsetY += (dy / distance) * pushStrength
    }
  })

  // Clamp offsets
  const clampedX = Math.max(-60, Math.min(60, offsetX))
  const clampedY = Math.max(-50, Math.min(50, offsetY))

  // Only apply offset if significant
  const hasOffset = Math.abs(clampedX) > 10 || Math.abs(clampedY) > 10

  return {
    x: hasOffset ? clampedX : 0,
    y: hasOffset ? clampedY : 0,
    hasOffset,
  }
}

// Card label geometry (px)
const CARD_WIDTH = 150
const CARD_ARROW = 8 // mũi tên dưới thẻ, đầu mũi tên chỉ đúng vị trí marker
const CARD_MAX_HEIGHT = 95 // thẻ cao nhất (giá xuống 2 dòng) tính cả mũi tên
const PIN_RADIUS = 5
const LOT_GAP = 6 // khoảng hở giữa mũi tên thẻ và viền lô
const LOT_NEAR = 12 // marker sát mép lô vẫn tính là thuộc lô đó
const LOT_MAX_LIFT = 160 // lô quá lớn trên màn hình thì giữ thẻ tại chỗ

// Outer rings of polygon paths in container pixels
function getLotOutlines(paths, map) {
  const outlines = []

  paths.forEach((pathData) => {
    if (!pathData.geojson) return

    try {
      const geojson = typeof pathData.geojson === 'string'
        ? JSON.parse(pathData.geojson)
        : pathData.geojson
      const geometry = geojson.geometry || geojson
      const polygons = geometry.type === 'Polygon'
        ? [geometry.coordinates]
        : geometry.type === 'MultiPolygon' ? geometry.coordinates : []

      polygons.forEach((polygon) => {
        const ring = (polygon[0] || []).map(([lng, lat]) => map.latLngToContainerPoint([lat, lng]))
        if (ring.length >= 3) {
          outlines.push({ ring, strokeWidth: pathData.stroke_width || 3 })
        }
      })
    } catch {
      // GeoJSON lỗi đã được báo khi vẽ path
    }
  })

  return outlines
}

function isPointInRing(point, ring) {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i]
    const b = ring[j]
    if ((a.y > point.y) !== (b.y > point.y) &&
      point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside
    }
  }
  return inside
}

// Y-range of the part of segment a-b lying between x = left and x = right
function getSegmentYRange(a, b, left, right) {
  if (Math.max(a.x, b.x) < left || Math.min(a.x, b.x) > right) return null
  if (a.x === b.x) return { minY: Math.min(a.y, b.y), maxY: Math.max(a.y, b.y) }

  const yAt = (x) => a.y + ((b.y - a.y) * (x - a.x)) / (b.x - a.x)
  const y1 = yAt(Math.max(left, Math.min(a.x, b.x)))
  const y2 = yAt(Math.min(right, Math.max(a.x, b.x)))
  return { minY: Math.min(y1, y2), maxY: Math.max(y1, y2) }
}

// Lift a card label above the lot its marker sits on, so the card does not cover the lot outline.
// The offset is returned unchanged when the card does not touch any outline.
function liftCardOffLot(markerData, offset, lotOutlines, map) {
  if (!map || lotOutlines.length === 0) return offset

  const point = map.latLngToContainerPoint([
    parseFloat(markerData.latitude),
    parseFloat(markerData.longitude),
  ])
  const left = point.x + offset.x - CARD_WIDTH / 2
  const right = left + CARD_WIDTH
  const bottom = point.y + offset.y // đầu mũi tên của thẻ
  const top = bottom - CARD_MAX_HEIGHT

  let clearY = Infinity

  lotOutlines.forEach(({ ring, strokeWidth }) => {
    const edges = ring.map((a, i) => [a, ring[(i + 1) % ring.length]])
    const isOnLot = isPointInRing(point, ring) ||
      edges.some(([a, b]) => L.LineUtil.pointToSegmentDistance(point, a, b) <= LOT_NEAR)
    if (!isOnLot) return

    let outlineTop = Infinity
    let touchesCard = false
    edges.forEach(([a, b]) => {
      const range = getSegmentYRange(a, b, left, right)
      if (!range) return
      outlineTop = Math.min(outlineTop, range.minY)
      if (range.maxY >= top && range.minY <= bottom) touchesCard = true
    })

    if (touchesCard) {
      clearY = Math.min(clearY, outlineTop - strokeWidth / 2 - LOT_GAP)
    }
  })

  if (!(clearY < bottom) || point.y - clearY > LOT_MAX_LIFT) return offset

  return { x: offset.x, y: clearY - point.y, hasOffset: true }
}

// Whether a card label (card, connector or pin) covers a rectangle centred on `center`
function doesCardCoverRect(markerData, offset, center, width, height, map) {
  const point = map.latLngToContainerPoint([
    parseFloat(markerData.latitude),
    parseFloat(markerData.longitude),
  ])
  const halfWidth = width / 2 + PIN_RADIUS
  const halfHeight = height / 2 + PIN_RADIUS
  const tipX = point.x + offset.x
  const tipY = point.y + offset.y

  // Card body
  if (Math.abs(tipX - center.x) <= CARD_WIDTH / 2 + halfWidth &&
    center.y + halfHeight >= tipY - CARD_MAX_HEIGHT &&
    center.y - halfHeight <= tipY) {
    return true
  }

  // Connector from the arrow tip down to the pin
  const steps = Math.ceil(Math.hypot(offset.x, offset.y) / 4)
  for (let i = 0; i <= steps; i++) {
    const t = steps ? i / steps : 0
    if (Math.abs(point.x + offset.x * t - center.x) <= halfWidth &&
      Math.abs(point.y + offset.y * t - center.y) <= halfHeight) {
      return true
    }
  }

  return false
}

// Create card marker HTML (matching MarketMapLeaflet style)
function createCardMarkerHTML(markerData, isSelected = false) {
  const name = markerData.name || 'Điểm đánh dấu'
  const color = markerData.marker_color || '#3B82F6'
  const isFeatured = markerData.is_featured

  // Get price/rent text
  let priceText = ''
  if (markerData.price_text) {
    priceText = markerData.price_text
  } else if (markerData.rent_price_text) {
    priceText = markerData.rent_price_text
  } else if (markerData.label_text) {
    priceText = markerData.label_text
  }

  // Property type icon
  const propertyIcons = {
    can_ho: '🏢',
    nha_pho: '🏠',
    biet_thu: '🏡',
    dat_nen: '🌍',
    mat_bang: '🏪',
    van_phong: '💼',
    phong_tro: '🛏️',
    shophouse: '🏬',
    custom: '📍',
  }
  const propertyIcon = propertyIcons[markerData.property_type] || propertyIcons.custom

  return `
    <div class="card-marker ${isSelected ? 'selected' : ''}" style="
      position: relative;
      filter: drop-shadow(0 3px 8px rgba(0,0,0,0.25));
      transition: all 0.2s ease;
      ${isSelected ? 'transform: scale(1.08);' : ''}
    ">
      <div style="
        background: white;
        border-radius: 10px;
        overflow: hidden;
        min-width: 125px;
        max-width: 160px;
        border: 2.5px solid ${isSelected ? '#2563eb' : color};
        ${isSelected ? 'box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.25);' : ''}
      ">
        <!-- Header -->
        <div style="
          background: linear-gradient(135deg, ${color} 0%, ${adjustColor(color, -20)} 100%);
          padding: 6px 10px;
          display: flex;
          align-items: center;
          gap: 5px;
        ">
          ${isFeatured ? `<span style="font-size: 11px;">⭐</span>` : `<span style="font-size: 10px;">${propertyIcon}</span>`}
          <span style="
            color: white;
            font-size: 11px;
            font-weight: 600;
            line-height: 1.3;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            flex: 1;
            text-shadow: 0 1px 2px rgba(0,0,0,0.2);
          ">${name}</span>
        </div>

        <!-- Price Section -->
        ${priceText ? `
          <div style="
            padding: 8px 10px;
            background: white;
            text-align: center;
          ">
            <div style="
              color: ${color};
              font-size: 13px;
              font-weight: 700;
              letter-spacing: -0.3px;
            ">${priceText}</div>
          </div>
        ` : ''}
      </div>

      <!-- Arrow -->
      <div style="
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 8px solid ${isSelected ? '#2563eb' : color};
      "></div>
    </div>
  `
}

// Create card label icon with offset support
function createCardLabelIcon(markerData, isSelected = false, offset = { x: 0, y: 0, hasOffset: false }) {
  const html = createCardMarkerHTML(markerData, isSelected)
  const color = markerData.marker_color || '#3B82F6'

  const width = CARD_WIDTH
  const height = markerData.price_text || markerData.rent_price_text || markerData.label_text ? 75 : 45

  // Connector line from the card's arrow to the pin when offset (stops at the pin's edge)
  const distance = Math.hypot(offset.x, offset.y)
  const reach = distance > PIN_RADIUS ? (distance - PIN_RADIUS) / distance : 0
  const connectorHtml = offset.hasOffset ? `
    <div class="connector-container" style="
      position: absolute;
      left: 50%;
      top: calc(100% + ${CARD_ARROW}px);
      width: 0;
      height: 0;
      pointer-events: none;
      z-index: -1;
    ">
      <svg width="1" height="1" style="
        position: absolute;
        left: 0;
        top: 0;
        overflow: visible;
      ">
        <line
          x1="0"
          y1="0"
          x2="${-offset.x * reach}"
          y2="${-offset.y * reach}"
          stroke="white"
          stroke-width="4"
          stroke-opacity="0.9"
          stroke-linecap="round"
        />
        <line
          x1="0"
          y1="0"
          x2="${-offset.x * reach}"
          y2="${-offset.y * reach}"
          stroke="${color}"
          stroke-width="2"
          stroke-dasharray="4,4"
        />
      </svg>
    </div>
  ` : ''

  // The card is anchored by its bottom so the arrow tip sits on the marker
  // whatever the card's height (a long price wraps to two lines)
  return L.divIcon({
    className: `card-marker-wrapper ${offset.hasOffset ? 'has-offset' : ''}`,
    html: `
      <div class="card-label-container" style="
        position: absolute;
        left: 0;
        bottom: ${CARD_ARROW}px;
        width: 100%;
        transform: translate(${offset.x}px, ${offset.y}px);
        transition: transform 0.3s ease;
      ">
        ${html}
        ${connectorHtml}
      </div>
    `,
    iconSize: [width, height],
    iconAnchor: [width / 2, height],
    popupAnchor: [offset.x, offset.y - height],
  })
}

// Create popup content - Professional styled popup
function createPopupContent(marker, getMarkerColor) {
  const markerColor = getMarkerColor(marker)

  // Helper to get marker type label
  const typeLabels = {
    custom: 'Tự do',
    ban: 'Bán',
    thue: 'Cho thuê',
    both: 'Bán & Thuê'
  }

  const typeIcons = {
    ban: `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>`,
    thue: `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>`,
    both: `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/></svg>`,
    custom: `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`
  }

  const hasImage = !!marker.image_url
  const hasPrice = marker.price_text || marker.rent_price_text

  return `
    <div class="marker-popup-content" style="
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      width: 300px;
      overflow: hidden;
    ">
      <!-- Image Section with Gradient Overlay -->
      ${hasImage ? `
        <div style="
          position: relative;
          margin: -14px -14px 0 -14px;
          height: 155px;
          overflow: hidden;
        ">
          <img src="${marker.image_url}" alt="${marker.name}"
               style="width: 100%; height: 100%; object-fit: cover; display: block;"
               onerror="this.parentElement.style.display='none'" />
          <div style="
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 85px;
            background: linear-gradient(to top, rgba(0,0,0,0.75), transparent);
          "></div>
          <!-- Type Badge -->
          <div style="
            position: absolute;
            top: 10px;
            left: 10px;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 5px 10px;
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.4px;
            text-transform: uppercase;
            color: white;
            background: ${markerColor};
            border-radius: 6px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          ">
            ${typeIcons[marker.marker_type] || typeIcons.custom}
            ${typeLabels[marker.marker_type] || 'Khác'}
          </div>
          <!-- Title on Image -->
          <h3 style="
            position: absolute;
            bottom: 10px;
            left: 14px;
            right: 14px;
            font-weight: 700;
            font-size: 17px;
            line-height: 1.25;
            margin: 0;
            color: white;
            padding-left: 5px;
            text-shadow: 0 1px 4px rgba(0,0,0,0.5);
          ">${marker.name}</h3>
        </div>
      ` : `
        <!-- Header without Image -->
        <div style="
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 12px;
          padding-bottom: 12px;
          border-bottom: 1px solid #E5E7EB;
        ">
          <h3 style="
            font-weight: 700;
            font-size: 16px;
            line-height: 1.3;
            margin: 0;
            color: #111827;
            flex: 1;
          ">${marker.name}</h3>
          <span style="
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 4px 8px;
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.3px;
            text-transform: uppercase;
            color: white;
            background: ${markerColor};
            border-radius: 5px;
            white-space: nowrap;
            margin-right: 30px;
          ">
            ${typeIcons[marker.marker_type] || typeIcons.custom}
            ${typeLabels[marker.marker_type] || 'Khác'}
          </span>
        </div>
      `}

      <!-- Content Section -->
      <div style="padding-top: ${hasImage ? '12px' : '0'};">
        <!-- Address -->
        ${marker.address ? `
          <div style="
            display: flex;
            align-items: flex-start;
            gap: 8px;
            margin-bottom: 12px;
          ">
            <div style="
              flex-shrink: 0;
              width: 26px;
              height: 26px;
              display: flex;
              align-items: center;
              justify-content: center;
              background: #F3F4F6;
              border-radius: 6px;
            ">
              <svg style="width: 13px; height: 13px; color: #6B7280;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            </div>
            <span style="font-size: 12px; color: #4B5563; line-height: 1.5; padding-top: 3px;">${marker.address}</span>
          </div>
        ` : ''}

        <!-- Price Section -->
        ${marker.marker_type === 'custom' ? `
          ${marker.label_text ? `
            <div style="
              position: relative;
              background: linear-gradient(135deg, ${markerColor}10, ${markerColor}05);
              padding: 12px 14px 12px 18px;
              border-radius: 10px;
              border: 1px solid ${markerColor}20;
              margin-bottom: 12px;
            ">
              <div style="
                position: absolute;
                top: 0;
                left: 0;
                bottom: 0;
                width: 4px;
                background: ${markerColor};
                border-radius: 10px 0 0 10px;
              "></div>
              <span style="color: #1F2937; font-weight: 600; font-size: 14px;">
                ${marker.label_text}
              </span>
            </div>
          ` : ''}
        ` : `
          ${hasPrice ? `
            <div style="
              display: grid;
              grid-template-columns: ${marker.price_text && marker.rent_price_text ? '1fr 1fr' : '1fr'};
              gap: 8px;
              margin-bottom: 12px;
            ">
              ${marker.price_text ? `
                <div style="
                  position: relative;
                  padding: 12px 14px;
                  background: linear-gradient(145deg, #FEF2F2, #FEE2E250);
                  border: 1px solid #FECACA;
                  border-radius: 10px;
                  text-align: center;
                  overflow: hidden;
                ">
                  <div style="
                    position: absolute;
                    top: -5px;
                    right: -5px;
                    width: 35px;
                    height: 35px;
                    background: #DC262610;
                    border-radius: 50%;
                  "></div>
                  <div style="
                    font-size: 9px;
                    color: #9CA3AF;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                    font-weight: 600;
                    margin-bottom: 4px;
                  ">Giá bán</div>
                  <div style="
                    color: #DC2626;
                    font-weight: 800;
                    font-size: 15px;
                    letter-spacing: -0.3px;
                  ">${marker.price_text}</div>
                </div>
              ` : ''}
              ${marker.rent_price_text ? `
                <div style="
                  position: relative;
                  padding: 12px 14px;
                  background: linear-gradient(145deg, #EFF6FF, #BFDBFE50);
                  border: 1px solid #BFDBFE;
                  border-radius: 10px;
                  text-align: center;
                  overflow: hidden;
                ">
                  <div style="
                    position: absolute;
                    top: -5px;
                    right: -5px;
                    width: 35px;
                    height: 35px;
                    background: #2563EB10;
                    border-radius: 50%;
                  "></div>
                  <div style="
                    font-size: 9px;
                    color: #9CA3AF;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                    font-weight: 600;
                    margin-bottom: 4px;
                  ">Giá thuê</div>
                  <div style="
                    color: #2563EB;
                    font-weight: 800;
                    font-size: 15px;
                    letter-spacing: -0.3px;
                  ">${marker.rent_price_text}</div>
                </div>
              ` : ''}
            </div>
          ` : ''}
        `}

        <!-- Description -->
        ${marker.description ? `
          <p style="
            font-size: 12px;
            color: #4B5563;
            margin: 0 0 12px 0;
            line-height: 1.55;
          ">
            ${marker.description.substring(0, 110)}${marker.description.length > 110 ? '...' : ''}
          </p>
        ` : ''}

        <!-- CTA Button -->
        ${marker.external_url ? `
          <a href="${marker.external_url}" target="_blank" rel="noopener noreferrer"
             style="
               display: flex;
               align-items: center;
               justify-content: center;
               gap: 6px;
               width: 100%;
               padding: 10px 14px;
               font-size: 12px;
               font-weight: 600;
               color: white;
               background: linear-gradient(135deg, ${markerColor}, ${markerColor}E0);
               border-radius: 8px;
               text-decoration: none;
               box-shadow: 0 2px 6px ${markerColor}35;
             ">
            Xem chi tiết
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </a>
        ` : ''}
      </div>
    </div>
  `
}

// Create path popup content
function createPathPopupContent(path) {
  return `
    <div style="font-family: system-ui, -apple-system, sans-serif; min-width: 150px;">
      <h3 style="font-weight: 600; font-size: 14px; margin: 0 0 4px 0; color: #111827;">
        ${path.name || (path.shape_type === 'polygon' ? 'Khu vực' : 'Đường đi')}
      </h3>
      ${path.layer_name ? `
        <p style="font-size: 12px; color: #6B7280; margin: 0;">
          ${path.layer_name}
        </p>
      ` : ''}
    </div>
  `
}

// Get dash array for stroke style
function getDashArray(strokeStyle, strokeWidth) {
  switch (strokeStyle) {
    case 'dashed':
      return `${strokeWidth * 3} ${strokeWidth * 2}`
    case 'dotted':
      return `${strokeWidth} ${strokeWidth * 2}`
    default:
      return null
  }
}

// Create path label icon for displaying name on polygon/path
function createPathLabelIcon(name, color, showConnector = false) {
  // Estimate label dimensions based on text length
  const charWidth = 7.5 // approximate px per character
  const padding = 24 // padding + border
  const estimatedWidth = name.length * charWidth + padding
  const estimatedHeight = 24

  return L.divIcon({
    className: 'path-label-wrapper',
    html: `
      <div style="
        display: inline-flex;
        align-items: center;
        padding: 4px 10px;
        background: rgba(255, 255, 255, 0.95);
        border: 2px solid ${color};
        border-radius: 6px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.15);
        color: ${color};
        font-size: 12px;
        font-weight: 600;
        white-space: nowrap;
        pointer-events: none;
        position: relative;
      ">${name}${showConnector ? `
        <div style="
          position: absolute;
          bottom: -6px;
          left: 50%;
          transform: translateX(-50%);
          width: 8px;
          height: 8px;
          background: ${color};
          border-radius: 50%;
          border: 2px solid white;
        "></div>
      ` : ''}</div>
    `,
    iconSize: [estimatedWidth, estimatedHeight],
    iconAnchor: [estimatedWidth / 2, estimatedHeight / 2],
  })
}

// Create connector line from label to path anchor point
function createLabelConnector(labelPos, anchorPos, color) {
  return L.polyline([labelPos, anchorPos], {
    color: color,
    weight: 2,
    opacity: 0.6,
    dashArray: '4, 4',
    interactive: false,
  })
}

// Get all vertices of a polygon for anchor point selection
function getPolygonVertices(coordinates) {
  if (!coordinates || !coordinates.length) return []

  let coords = coordinates
  if (coords[0] && Array.isArray(coords[0][0])) {
    coords = coords[0] // Get outer ring
  }

  return coords
    .filter(coord => Array.isArray(coord) && coord.length >= 2)
    .map(coord => [coord[1], coord[0]]) // Convert to [lat, lng]
}

// Get all vertices of a polyline
function getPolylineVertices(coordinates) {
  if (!coordinates || coordinates.length === 0) return []

  return coordinates
    .filter(coord => Array.isArray(coord) && coord.length >= 2)
    .map(coord => [coord[1], coord[0]]) // Convert to [lat, lng]
}

// Calculate centroid of polygon
function getPolygonCentroid(coordinates) {
  const vertices = getPolygonVertices(coordinates)
  if (vertices.length === 0) return null

  let sumLat = 0
  let sumLng = 0

  vertices.forEach(([lat, lng]) => {
    sumLat += lat
    sumLng += lng
  })

  return [sumLat / vertices.length, sumLng / vertices.length]
}

// Calculate bounding box of polygon/path
function getBoundingBox(vertices) {
  if (!vertices || vertices.length === 0) return null

  let minLat = Infinity, maxLat = -Infinity
  let minLng = Infinity, maxLng = -Infinity

  vertices.forEach(([lat, lng]) => {
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
  })

  return { minLat, maxLat, minLng, maxLng }
}

// Get candidate positions for label (multiple positions to try)
function getLabelCandidatePositions(geometry, shapeType) {
  const candidates = []

  if (shapeType === 'polygon' || geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
    const coords = geometry.type === 'Polygon'
      ? geometry.coordinates
      : geometry.coordinates[0]

    const vertices = getPolygonVertices(coords)
    const centroid = getPolygonCentroid(coords)
    const bbox = getBoundingBox(vertices)

    if (centroid) {
      // Priority 1: Centroid (best for regular shapes)
      candidates.push({ pos: centroid, priority: 1, type: 'centroid' })
    }

    if (bbox) {
      // Priority 2: Top center (good visibility)
      candidates.push({
        pos: [bbox.maxLat, (bbox.minLng + bbox.maxLng) / 2],
        priority: 2,
        type: 'top'
      })

      // Priority 3: Bottom center
      candidates.push({
        pos: [bbox.minLat, (bbox.minLng + bbox.maxLng) / 2],
        priority: 3,
        type: 'bottom'
      })

      // Priority 4: Left center
      candidates.push({
        pos: [(bbox.minLat + bbox.maxLat) / 2, bbox.minLng],
        priority: 4,
        type: 'left'
      })

      // Priority 5: Right center
      candidates.push({
        pos: [(bbox.minLat + bbox.maxLat) / 2, bbox.maxLng],
        priority: 5,
        type: 'right'
      })

      // Priority 6-9: Corner positions
      candidates.push({ pos: [bbox.maxLat, bbox.minLng], priority: 6, type: 'top-left' })
      candidates.push({ pos: [bbox.maxLat, bbox.maxLng], priority: 7, type: 'top-right' })
      candidates.push({ pos: [bbox.minLat, bbox.minLng], priority: 8, type: 'bottom-left' })
      candidates.push({ pos: [bbox.minLat, bbox.maxLng], priority: 9, type: 'bottom-right' })
    }

  } else if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
    const coords = geometry.type === 'LineString'
      ? geometry.coordinates
      : geometry.coordinates[0]

    const vertices = getPolylineVertices(coords)

    if (vertices.length > 0) {
      // Priority 1: Middle point of line
      const midIndex = Math.floor(vertices.length / 2)
      candidates.push({ pos: vertices[midIndex], priority: 1, type: 'middle' })

      // Priority 2: 1/3 point
      const thirdIndex = Math.floor(vertices.length / 3)
      if (thirdIndex !== midIndex) {
        candidates.push({ pos: vertices[thirdIndex], priority: 2, type: 'third' })
      }

      // Priority 3: 2/3 point
      const twoThirdIndex = Math.floor((vertices.length * 2) / 3)
      if (twoThirdIndex !== midIndex) {
        candidates.push({ pos: vertices[twoThirdIndex], priority: 3, type: 'two-third' })
      }

      // Priority 4-5: Start and end points
      candidates.push({ pos: vertices[0], priority: 4, type: 'start' })
      candidates.push({ pos: vertices[vertices.length - 1], priority: 5, type: 'end' })
    }
  }

  return candidates.sort((a, b) => a.priority - b.priority)
}

// Estimate label bounding box in lat/lng based on map zoom
function estimateLabelBounds(position, labelName, map) {
  if (!map) return null

  const charWidth = 7.5
  const padding = 24
  const labelWidthPx = labelName.length * charWidth + padding
  const labelHeightPx = 24

  // Convert pixel dimensions to lat/lng at current zoom
  const point = map.latLngToContainerPoint(position)

  const topLeft = map.containerPointToLatLng([
    point.x - labelWidthPx / 2,
    point.y - labelHeightPx / 2
  ])
  const bottomRight = map.containerPointToLatLng([
    point.x + labelWidthPx / 2,
    point.y + labelHeightPx / 2
  ])

  return {
    minLat: bottomRight.lat,
    maxLat: topLeft.lat,
    minLng: topLeft.lng,
    maxLng: bottomRight.lng,
    centerLat: position[0],
    centerLng: position[1]
  }
}

// Check if two bounding boxes overlap
function boundsOverlap(bounds1, bounds2, padding = 0.00005) {
  if (!bounds1 || !bounds2) return false

  return !(
    bounds1.maxLng + padding < bounds2.minLng ||
    bounds1.minLng - padding > bounds2.maxLng ||
    bounds1.maxLat + padding < bounds2.minLat ||
    bounds1.minLat - padding > bounds2.maxLat
  )
}

// Calculate polygon area in pixels (rough estimate)
function getPolygonPixelArea(geometry, map) {
  if (!map || !geometry) return Infinity

  const coords = geometry.type === 'Polygon'
    ? geometry.coordinates[0]
    : geometry.type === 'MultiPolygon'
      ? geometry.coordinates[0][0]
      : null

  if (!coords || coords.length < 3) return Infinity

  // Convert to pixel coordinates
  const pixelCoords = coords.map(coord => {
    const point = map.latLngToContainerPoint([coord[1], coord[0]])
    return [point.x, point.y]
  })

  // Calculate area using shoelace formula
  let area = 0
  for (let i = 0; i < pixelCoords.length - 1; i++) {
    area += pixelCoords[i][0] * pixelCoords[i + 1][1]
    area -= pixelCoords[i + 1][0] * pixelCoords[i][1]
  }
  return Math.abs(area) / 2
}

// Smart label positioning class to manage all labels
class SmartLabelManager {
  constructor(map) {
    this.map = map
    this.placedLabels = [] // Array of { bounds, pathId, position }
  }

  // Check if polygon is too small for label inside
  isSmallPolygon(geometry, labelName) {
    const charWidth = 7.5
    const padding = 24
    const labelWidth = labelName.length * charWidth + padding
    const labelHeight = 24
    const labelArea = labelWidth * labelHeight

    const polygonArea = getPolygonPixelArea(geometry, this.map)

    // If polygon area < 2x label area, consider it small
    return polygonArea < labelArea * 2
  }

  // Find best position for a new label that doesn't overlap with existing labels
  // Returns { position, anchorPoint, needsConnector }
  findBestPosition(pathData, geometry) {
    const candidates = getLabelCandidatePositions(geometry, pathData.shape_type)
    const centroid = candidates.length > 0 ? candidates[0].pos : null

    // Check if this is a small polygon - if so, prefer outside positions
    const isPolygon = geometry.type === 'Polygon' || geometry.type === 'MultiPolygon'
    const isSmall = isPolygon && this.isSmallPolygon(geometry, pathData.name)

    // Reorder candidates for small polygons: outside positions first
    let orderedCandidates = candidates
    if (isSmall) {
      // Move outside positions (top, bottom, left, right, corners) before centroid
      orderedCandidates = [...candidates].sort((a, b) => {
        const outsideTypes = ['top', 'bottom', 'left', 'right', 'top-left', 'top-right', 'bottom-left', 'bottom-right']
        const aIsOutside = outsideTypes.includes(a.type) ? 0 : 1
        const bIsOutside = outsideTypes.includes(b.type) ? 0 : 1
        if (aIsOutside !== bIsOutside) return aIsOutside - bIsOutside
        return a.priority - b.priority
      })
    }

    for (const candidate of orderedCandidates) {
      const bounds = estimateLabelBounds(candidate.pos, pathData.name, this.map)

      if (!bounds) continue

      // Check overlap with all placed labels
      let hasOverlap = false
      for (const placed of this.placedLabels) {
        if (boundsOverlap(bounds, placed.bounds)) {
          hasOverlap = true
          break
        }
      }

      if (!hasOverlap) {
        // Found a non-overlapping position
        this.placedLabels.push({
          bounds,
          pathId: pathData.id,
          position: candidate.pos,
          type: candidate.type
        })

        // Check if this position is far from centroid (needs connector)
        // For small polygons, always show connector if not at centroid
        const needsConnector = centroid && (
          candidate.type !== 'centroid' && candidate.type !== 'middle'
        )
        return {
          position: candidate.pos,
          anchorPoint: centroid,
          needsConnector: isSmall || needsConnector
        }
      }
    }

    // If all candidates overlap, try to find the position with least overlap
    // by offsetting from centroid
    if (candidates.length > 0) {
      const offsets = [
        [0.0003, 0], [-0.0003, 0], [0, 0.0003], [0, -0.0003],
        [0.0004, 0.0002], [-0.0004, 0.0002], [0.0004, -0.0002], [-0.0004, -0.0002],
        [0.0005, 0], [-0.0005, 0], [0, 0.0005], [0, -0.0005]
      ]

      for (const [latOff, lngOff] of offsets) {
        const offsetPos = [centroid[0] + latOff, centroid[1] + lngOff]
        const bounds = estimateLabelBounds(offsetPos, pathData.name, this.map)

        if (!bounds) continue

        let hasOverlap = false
        for (const placed of this.placedLabels) {
          if (boundsOverlap(bounds, placed.bounds)) {
            hasOverlap = true
            break
          }
        }

        if (!hasOverlap) {
          this.placedLabels.push({
            bounds,
            pathId: pathData.id,
            position: offsetPos,
            type: 'offset'
          })
          return {
            position: offsetPos,
            anchorPoint: centroid,
            needsConnector: true // Offset positions always need connector
          }
        }
      }

      // Last resort: use centroid anyway (will overlap but no connector needed)
      const bounds = estimateLabelBounds(centroid, pathData.name, this.map)
      this.placedLabels.push({
        bounds,
        pathId: pathData.id,
        position: centroid,
        type: 'fallback'
      })
      return {
        position: centroid,
        anchorPoint: centroid,
        needsConnector: false
      }
    }

    return null
  }

  // Reset for new render
  reset() {
    this.placedLabels = []
  }
}

const LeafletMapPublicView = forwardRef(function LeafletMapPublicView(
  {
    markers = [],
    paths = [], // NEW: paths/shapes to display
    selectedMarkerId,
    onMarkerClick,
    onPathClick,
    center = { lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1] },
    zoom = DEFAULT_ZOOM,
    mapStyle = 'roadmap',
    filterType = 'all',
    getMarkerColor,
  },
  ref
) {
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const markersLayerRef = useRef(null)
  const pathsLayerRef = useRef(null) // layer for paths
  const pathLabelsLayerRef = useRef(null) // layer for path labels
  const markersMapRef = useRef(new Map())
  const pathsMapRef = useRef(new Map()) // map for path layers
  const pathLabelsRef = useRef([]) // placed path labels: { layers, position, width, height }
  const [mapLoaded, setMapLoaded] = useState(false)
  const [currentZoom, setCurrentZoom] = useState(zoom)

  // Zoom thresholds for marker display modes
  const ZOOM_SHOW_CARDS = 14 // Above this: show card markers
  const ZOOM_SHOW_ICONS = 12 // Above this but below CARDS: show icon markers for non-overlapping
  // Below SHOW_ICONS: cluster/hide overlapping markers

  // Convert center format
  const centerLatLng = Array.isArray(center)
    ? center
    : [center.lat || DEFAULT_CENTER[0], center.lng || DEFAULT_CENTER[1]]

  // Convert Google map style to Leaflet tile layer
  const tileLayerKey = GOOGLE_STYLE_MAP[mapStyle] || 'cartodb_light'

  // Expose map methods via ref
  useImperativeHandle(ref, () => ({
    getMap: () => mapRef.current,
    panTo: (lat, lng, targetZoom) => {
      if (mapRef.current) {
        mapRef.current.flyTo([lat, lng], targetZoom || mapRef.current.getZoom(), {
          duration: 1,
        })
      }
    },
    setZoom: (targetZoom) => {
      if (mapRef.current) {
        mapRef.current.setZoom(targetZoom)
      }
    },
    openMarkerPopup: (markerId) => {
      const marker = markersMapRef.current.get(markerId)
      if (marker) {
        marker.openPopup()
      }
    },
    fitToPath: (pathId) => {
      const pathLayer = pathsMapRef.current.get(pathId)
      if (pathLayer && mapRef.current) {
        mapRef.current.fitBounds(pathLayer.getBounds(), { padding: [50, 50] })
      }
    },
  }))

  // Default getMarkerColor if not provided
  const resolveMarkerColor = useCallback(
    (marker) => {
      if (getMarkerColor) return getMarkerColor(marker)
      if (marker.marker_color) return marker.marker_color
      switch (marker.marker_type) {
        case 'ban':
          return '#EF4444'
        case 'thue':
          return '#3B82F6'
        case 'both':
          return '#8B5CF6'
        case 'custom':
          return '#10B981'
        default:
          return '#6B7280'
      }
    },
    [getMarkerColor]
  )

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    // Inject path animation styles
    injectPathStyles()

    const map = L.map(containerRef.current, {
      center: centerLatLng,
      zoom: zoom,
      zoomControl: true,
      attributionControl: true,
    })

    mapRef.current = map

    // Add tile layer
    const tileConfig = TILE_LAYERS[tileLayerKey] || TILE_LAYERS.cartodb_light
    L.tileLayer(tileConfig.url, {
      attribution: tileConfig.attribution,
      maxZoom: 19,
    }).addTo(map)

    // Create paths layer group (below markers)
    pathsLayerRef.current = L.layerGroup().addTo(map)

    // Create path labels layer group (above paths, below markers)
    pathLabelsLayerRef.current = L.layerGroup().addTo(map)

    // Create markers layer group (on top)
    markersLayerRef.current = L.layerGroup().addTo(map)

    // Listen to zoom changes
    map.on('zoomend', () => {
      setCurrentZoom(map.getZoom())
    })

    setMapLoaded(true)

    // Cleanup
    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
      markersMapRef.current.clear()
      pathsMapRef.current.clear()
    }
  }, [])

  // Update tile layer when mapStyle changes
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return

    const map = mapRef.current
    const tileConfig = TILE_LAYERS[tileLayerKey] || TILE_LAYERS.cartodb_light

    // Remove existing tile layers
    map.eachLayer((layer) => {
      if (layer instanceof L.TileLayer) {
        map.removeLayer(layer)
      }
    })

    // Add new tile layer (below everything)
    const tileLayer = L.tileLayer(tileConfig.url, {
      attribution: tileConfig.attribution,
      maxZoom: 19,
    })
    tileLayer.addTo(map)
    tileLayer.bringToBack()

  }, [tileLayerKey, mapLoaded])

  // Update paths when data changes
  useEffect(() => {
    if (!mapLoaded || !pathsLayerRef.current || !pathLabelsLayerRef.current || !mapRef.current) return

    const pathsLayer = pathsLayerRef.current
    const labelsLayer = pathLabelsLayerRef.current
    const map = mapRef.current

    // Clear existing paths and labels
    pathsLayer.clearLayers()
    labelsLayer.clearLayers()
    pathsMapRef.current.clear()
    pathLabelsRef.current = []

    // Initialize smart label manager for collision detection
    const labelManager = new SmartLabelManager(map)

    // First pass: render all paths
    const pathsToLabel = []

    paths.forEach((pathData) => {
      if (!pathData.geojson) return

      try {
        const geojson = typeof pathData.geojson === 'string'
          ? JSON.parse(pathData.geojson)
          : pathData.geojson

        // Style options
        const strokeColor = pathData.stroke_color || '#3B82F6'
        const dashArray = getDashArray(pathData.stroke_style, pathData.stroke_width)
        const style = {
          color: strokeColor,
          weight: pathData.stroke_width || 3,
          opacity: pathData.stroke_opacity || 1,
          fillColor: pathData.fill_color || '#DBEAFE',
          fillOpacity: pathData.shape_type === 'polygon' ? (pathData.fill_opacity || 0.3) : 0,
          dashArray: dashArray,
          lineCap: 'round',
          lineJoin: 'round',
        }

        // Create layer from GeoJSON
        const layer = L.geoJSON(geojson, {
          style: () => style,
          onEachFeature: (_feature, lyr) => {
            // Add popup
            lyr.bindPopup(createPathPopupContent(pathData), {
              maxWidth: 200,
              className: 'path-popup',
            })

            // Click handler
            lyr.on('click', () => {
              if (onPathClick) {
                onPathClick(pathData)
              }
            })

            // Apply animation class if needed
            if (pathData.animation && pathData.animation !== 'none') {
              lyr.on('add', () => {
                const element = lyr.getElement()
                if (element) {
                  element.classList.add(`path-animation-${pathData.animation}`)
                  element.style.setProperty('--base-stroke-width', `${pathData.stroke_width || 3}`)
                  element.style.setProperty('--stroke-color', strokeColor)
                }
              })
            }
          },
        })

        // Store reference
        pathsMapRef.current.set(pathData.id, layer)
        pathsLayer.addLayer(layer)

        // Collect paths that need labels
        // Check show_label setting for all shape types (default true)
        const shouldShowLabel = pathData.name && geojson.geometry && pathData.show_label !== false

        if (shouldShowLabel) {
          pathsToLabel.push({
            pathData,
            geometry: geojson.geometry,
            strokeColor
          })
        }

      } catch (error) {
        console.error('Failed to render path:', pathData.id, error)
      }
    })

    // Second pass: add labels with smart positioning (no overlap)
    // Sort by area/length to prioritize larger shapes first
    pathsToLabel.sort((a, b) => {
      // Polygons first, then by name length (shorter names = higher priority)
      const aIsPoly = a.geometry.type.includes('Polygon') ? 0 : 1
      const bIsPoly = b.geometry.type.includes('Polygon') ? 0 : 1
      if (aIsPoly !== bIsPoly) return aIsPoly - bIsPoly
      return a.pathData.name.length - b.pathData.name.length
    })

    pathsToLabel.forEach(({ pathData, geometry, strokeColor }) => {
      const result = labelManager.findBestPosition(pathData, geometry)

      if (result && result.position) {
        const layers = []

        // Add connector line if label is offset from its path
        if (result.needsConnector && result.anchorPoint) {
          const connector = createLabelConnector(result.position, result.anchorPoint, strokeColor)
          labelsLayer.addLayer(connector)
          layers.push(connector)
        }

        // Add label marker
        const labelIcon = createPathLabelIcon(pathData.name, strokeColor, result.needsConnector)
        const labelMarker = L.marker(result.position, {
          icon: labelIcon,
          interactive: false, // Don't intercept clicks
          zIndexOffset: 500,
        })
        labelsLayer.addLayer(labelMarker)
        layers.push(labelMarker)

        const [width, height] = labelIcon.options.iconSize
        pathLabelsRef.current.push({ layers, position: result.position, width, height })
      }
    })

  }, [paths, mapLoaded, onPathClick])

  // Detect overlapping markers and determine which ones to show as cards vs icons
  const detectOverlappingMarkers = useCallback((markersData, map, zoomLevel) => {
    if (!map || markersData.length === 0) return { cards: [], icons: [] }

    // At high zoom, show all as cards
    if (zoomLevel >= ZOOM_SHOW_CARDS) {
      return { cards: markersData, icons: [] }
    }

    // At very low zoom, show all as icons
    if (zoomLevel < ZOOM_SHOW_ICONS) {
      return { cards: [], icons: markersData }
    }

    // Medium zoom: check for overlaps, show cards for non-overlapping, icons for overlapping
    const cardWidth = 150
    const cardHeight = 75

    const cards = []
    const icons = []
    const occupiedBounds = []

    // Sort by priority: selected > featured > others
    const sortedMarkers = [...markersData].sort((a, b) => {
      if (a.id === selectedMarkerId) return -1
      if (b.id === selectedMarkerId) return 1
      if (a.is_featured && !b.is_featured) return -1
      if (!a.is_featured && b.is_featured) return 1
      return 0
    })

    sortedMarkers.forEach((marker) => {
      // If show_label is explicitly false, always show as icon
      if (marker.show_label === false) {
        icons.push(marker)
        return
      }

      const point = map.latLngToContainerPoint([
        parseFloat(marker.latitude),
        parseFloat(marker.longitude),
      ])

      const markerBounds = {
        left: point.x - cardWidth / 2,
        right: point.x + cardWidth / 2,
        top: point.y - cardHeight,
        bottom: point.y,
      }

      // Check if overlaps with any existing card
      let hasOverlap = false
      for (const occupied of occupiedBounds) {
        const overlapX = Math.max(0, Math.min(markerBounds.right, occupied.right) - Math.max(markerBounds.left, occupied.left))
        const overlapY = Math.max(0, Math.min(markerBounds.bottom, occupied.bottom) - Math.max(markerBounds.top, occupied.top))

        if (overlapX > 0 && overlapY > 0) {
          const overlapArea = overlapX * overlapY
          const markerArea = cardWidth * cardHeight
          if (overlapArea > markerArea * 0.3) { // 30% overlap
            hasOverlap = true
            break
          }
        }
      }

      if (!hasOverlap || marker.id === selectedMarkerId) {
        cards.push(marker)
        occupiedBounds.push(markerBounds)
      } else {
        icons.push(marker)
      }
    })

    return { cards, icons }
  }, [selectedMarkerId, ZOOM_SHOW_CARDS, ZOOM_SHOW_ICONS])

  // Update markers when data changes or zoom changes
  useEffect(() => {
    if (!mapLoaded || !markersLayerRef.current) return

    const map = mapRef.current
    const markersLayer = markersLayerRef.current

    // Clear existing markers
    markersLayer.clearLayers()
    markersMapRef.current.clear()

    // Filter markers
    const filteredMarkers = markers.filter((m) => {
      if (filterType === 'all') return true
      if (filterType === 'ban') return m.marker_type === 'ban' || m.marker_type === 'both'
      if (filterType === 'thue') return m.marker_type === 'thue' || m.marker_type === 'both'
      if (filterType === 'custom') return m.marker_type === 'custom'
      return true
    })

    // Detect overlaps and determine display mode for each marker
    const { cards, icons } = detectOverlappingMarkers(filteredMarkers, map, currentZoom)

    // Calculate offset for each card label (to avoid overlap with nearby markers),
    // then lift it off the outline of the lot the marker sits on
    const lotOutlines = getLotOutlines(paths, map)
    const cardOffsets = new Map()
    cards.forEach((markerData) => {
      if (!markerData.latitude || !markerData.longitude) return
      cardOffsets.set(
        markerData.id,
        liftCardOffLot(markerData, calculateLabelOffset(markerData, cards, map), lotOutlines, map)
      )
    })

    // Thẻ giá được ưu tiên hơn tên vùng: tên vùng chạm vào thẻ, đường nối hoặc điểm ghim thì ẩn đi
    pathLabelsRef.current.forEach(({ layers, position, width, height }) => {
      const center = map.latLngToContainerPoint(position)
      const isCovered = cards.some((markerData) => {
        const offset = cardOffsets.get(markerData.id)
        return offset && doesCardCoverRect(markerData, offset, center, width, height, map)
      })
      layers.forEach((layer) => {
        if (isCovered) {
          pathLabelsLayerRef.current.removeLayer(layer)
        } else {
          pathLabelsLayerRef.current.addLayer(layer)
        }
      })
    })

    // Create card markers (full display with name/price)
    cards.forEach((markerData) => {
      if (!markerData.latitude || !markerData.longitude) return

      const lat = parseFloat(markerData.latitude)
      const lng = parseFloat(markerData.longitude)
      const color = resolveMarkerColor(markerData)
      const isSelected = selectedMarkerId === markerData.id

      const offset = cardOffsets.get(markerData.id)
      const shouldOffset = offset.hasOffset

      // 1. Create PIN marker (always at exact coordinates - never moves)
      const pinMarker = L.marker([lat, lng], {
        icon: createPinIcon(color, isSelected),
        zIndexOffset: isSelected ? 900 : (markerData.is_featured ? 400 : -100),
        interactive: false,
      })

      // 2. Create CARD LABEL marker
      const labelMarker = L.marker([lat, lng], {
        icon: createCardLabelIcon(markerData, isSelected, shouldOffset ? offset : { x: 0, y: 0, hasOffset: false }),
        zIndexOffset: isSelected ? 1000 : (markerData.is_featured ? 500 : 0),
      })

      // Add popup to label marker
      labelMarker.bindPopup(createPopupContent(markerData, resolveMarkerColor), {
        maxWidth: 280,
        className: 'custom-popup',
      })

      // Click handler on label
      labelMarker.on('click', () => {
        if (onMarkerClick) {
          onMarkerClick(markerData)
        }
      })

      // Store reference
      labelMarker.markerData = markerData
      labelMarker.pinMarker = pinMarker
      labelMarker.currentOffset = shouldOffset ? offset : { x: 0, y: 0, hasOffset: false }
      labelMarker.displayMode = 'card'
      markersMapRef.current.set(markerData.id, labelMarker)

      // Add pin first (below), then label (on top)
      if (shouldOffset) {
        markersLayer.addLayer(pinMarker)
      }
      markersLayer.addLayer(labelMarker)
    })

    // Create icon markers (compact display - only colored dot)
    icons.forEach((markerData) => {
      if (!markerData.latitude || !markerData.longitude) return

      const lat = parseFloat(markerData.latitude)
      const lng = parseFloat(markerData.longitude)
      const color = resolveMarkerColor(markerData)
      const isSelected = selectedMarkerId === markerData.id

      // Create icon-only marker (larger pin when zoomed out)
      const iconMarker = L.marker([lat, lng], {
        icon: createPinIcon(color, isSelected),
        zIndexOffset: isSelected ? 800 : (markerData.is_featured ? 300 : -200),
      })

      // Add popup to icon marker
      iconMarker.bindPopup(createPopupContent(markerData, resolveMarkerColor), {
        maxWidth: 280,
        className: 'custom-popup',
      })

      // Click handler
      iconMarker.on('click', () => {
        if (onMarkerClick) {
          onMarkerClick(markerData)
        }
      })

      // Store reference
      iconMarker.markerData = markerData
      iconMarker.displayMode = 'icon'
      markersMapRef.current.set(markerData.id, iconMarker)

      markersLayer.addLayer(iconMarker)
    })
  }, [markers, paths, filterType, mapLoaded, selectedMarkerId, resolveMarkerColor, onMarkerClick, currentZoom, detectOverlappingMarkers])

  // Update selected marker style and open popup
  useEffect(() => {
    if (!mapLoaded) return

    markersMapRef.current.forEach((marker, id) => {
      const isSelected = selectedMarkerId === id
      const markerData = marker.markerData
      const color = resolveMarkerColor(markerData)

      if (marker.displayMode === 'card') {
        // Card mode: update card icon
        const offset = marker.currentOffset || { x: 0, y: 0, hasOffset: false }
        marker.setIcon(createCardLabelIcon(markerData, isSelected, offset))
        marker.setZIndexOffset(isSelected ? 1000 : (markerData.is_featured ? 500 : 0))

        // Update pin icon if exists
        if (marker.pinMarker) {
          marker.pinMarker.setIcon(createPinIcon(color, isSelected))
          marker.pinMarker.setZIndexOffset(isSelected ? 900 : (markerData.is_featured ? 400 : -100))
        }
      } else {
        // Icon mode: update pin icon
        marker.setIcon(createPinIcon(color, isSelected))
        marker.setZIndexOffset(isSelected ? 800 : (markerData.is_featured ? 300 : -200))
      }

      if (isSelected) {
        marker.openPopup()
      }
    })
  }, [selectedMarkerId, mapLoaded, resolveMarkerColor])

  return (
    <div className="w-full h-full relative">
      <div
        ref={containerRef}
        className="w-full h-full"
        style={{ minHeight: '300px', background: '#E8E0D5' }}
      />

      {/* Map type switcher */}
      {mapLoaded && (
        <div className="absolute top-3 right-3 z-[500] flex gap-1">
          {[
            { key: 'cartodb_light', label: 'Map' },
            { key: 'satellite', label: 'Vệ tinh' },
          ].map((layer) => (
            <button
              key={layer.key}
              onClick={() => {
                const map = mapRef.current
                if (!map) return

                // Update tile layer
                map.eachLayer((l) => {
                  if (l instanceof L.TileLayer) {
                    map.removeLayer(l)
                  }
                })

                const tileConfig = TILE_LAYERS[layer.key]
                const tileLayer = L.tileLayer(tileConfig.url, {
                  attribution: tileConfig.attribution,
                  maxZoom: 19,
                })
                tileLayer.addTo(map)
                tileLayer.bringToBack()
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors shadow-md ${
                tileLayerKey === layer.key
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {layer.label}
            </button>
          ))}
        </div>
      )}

      {/* Loading overlay */}
      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Đang tải bản đồ...</p>
          </div>
        </div>
      )}
    </div>
  )
})

export default LeafletMapPublicView
